// Chemin: C:\gestion-copro\src\constants\documentTaxonomy.ts
// Session 63 : Création taxonomie GED pilotée par SENTINELLE
// Session 64 : Ajout champ code aux CATEGORIES_DOCUMENT_OPTIONS
// Session 67 : Ajout types documents comptables (annexe, appel_fonds, budget, releve)
// Session 166 : Ajout catégorie "Mon bien / Location" + types documents bailleur
// Session 269 : Ajout type appel_loyer (workflow appel → quittance)
// Référence : CADRAGE_GED_SENTINELLE_V2.md, CADRAGE_MODULE_VERIFICATION_COMPTABLE_V1.md, CADRAGE_CHARGES_RECUPERABLES_BAILLEUR.md

// ============================================
// TYPES DE DOCUMENTS (29 types)
// ============================================

export const TYPES_DOCUMENT_GED = {
  pv: 'PV',
  contrat: 'Contrat',
  facture: 'Facture',
  devis: 'Devis',
  courrier: 'Courrier',
  email: 'Email',
  rapport: 'Rapport',
  plan: 'Plan',
  attestation: 'Attestation',
  reglement: 'Règlement',
  edd: 'EDD',
  dpe: 'DPE',
  permis: 'Permis',
  photo: 'Photo',
  note: 'Note',
  // Session 67 : Types comptables
  annexe: 'Annexe comptable',
  appel_fonds: 'Appel de fonds',
  budget: 'Budget prévisionnel',
  releve: 'Relevé',
  // Session 166 : Types documents bailleur
  bail: 'Bail',
  assurance_pno: 'Assurance PNO',
  diagnostic_locatif: 'Diagnostic locatif',
  taxe_fonciere: 'Taxe foncière',
  etat_lieux: 'État des lieux',
  appel_loyer: 'Appel de loyer',
  quittance_loyer: 'Quittance de loyer',
  regularisation_charges: 'Régularisation de charges',
  reglement_copropriete: 'Règlement de copropriété',
  autre: 'Autre',
} as const;

export type TypeDocumentGED = keyof typeof TYPES_DOCUMENT_GED;

export const TYPES_DOCUMENT_GED_OPTIONS: { value: TypeDocumentGED; label: string }[] = 
  Object.entries(TYPES_DOCUMENT_GED).map(([value, label]) => ({
    value: value as TypeDocumentGED,
    label,
  }));

// ============================================
// CATÉGORIES DE DOCUMENTS (11 catégories)
// ============================================

export const CATEGORIES_DOCUMENT = {
  juridique: 'Juridique',
  comptabilite: 'Comptabilité',
  travaux: 'Travaux',
  gpa: 'GPA',
  assurance: 'Assurance',
  syndic: 'Syndic',
  promoteur: 'Promoteur',
  technique: 'Technique',
  administratif: 'Administratif',
  interne_cs: 'Interne CS',
  // Session 166 : Catégorie bailleur
  mon_bien: 'Mon bien / Location',
} as const;

export type CategorieDocument = keyof typeof CATEGORIES_DOCUMENT;

// Codes courts pour le nommage normalisé (déplacé ici pour être utilisé dans OPTIONS)
export const CODES_CATEGORIES: Record<CategorieDocument, string> = {
  juridique: 'JUR',
  comptabilite: 'CPT',
  travaux: 'TRV',
  gpa: 'GPA',
  assurance: 'ASS',
  syndic: 'SYN',
  promoteur: 'PRO',
  technique: 'TEC',
  administratif: 'ADM',
  interne_cs: 'INT',
  // Session 166
  mon_bien: 'MON',
};

// Session 64 : Ajout du champ code pour l'affichage dans la GED
export const CATEGORIES_DOCUMENT_OPTIONS: { value: CategorieDocument; label: string; code: string }[] = 
  Object.entries(CATEGORIES_DOCUMENT).map(([value, label]) => ({
    value: value as CategorieDocument,
    label,
    code: CODES_CATEGORIES[value as CategorieDocument],
  }));

// ============================================
// COULEURS PAR CATÉGORIE (Session 166)
// ============================================

export const COULEURS_CATEGORIES: Record<CategorieDocument, string> = {
  juridique: 'blue',
  comptabilite: 'green',
  travaux: 'orange',
  gpa: 'red',
  assurance: 'purple',
  syndic: 'gray',
  promoteur: 'yellow',
  technique: 'cyan',
  administratif: 'slate',
  interne_cs: 'teal',
  mon_bien: 'indigo',
};

// ============================================
// SOUS-CATÉGORIES "MON BIEN / LOCATION" (Session 166)
// Pour le classement automatique SENTINELLE
// ============================================

export const SOUS_CATEGORIES_MON_BIEN = {
  bail: 'Contrat de bail',
  assurance_pno: 'Assurance propriétaire non-occupant',
  diagnostics_locatifs: 'Diagnostics locatifs (DPE, électricité, gaz, plomb, ERP)',
  taxe_fonciere: 'Avis d\'imposition foncier',
  etat_lieux: 'État des lieux entrée / sortie',
  appel_loyer: 'Appels de loyer émis au locataire',
  quittance_loyer: 'Quittances émises au locataire',
  regularisation_charges: 'Décompte charges récupérables',
  correspondance_locataire: 'Échanges avec le locataire',
  reglement_copropriete: 'Règlement de copropriété',
} as const;

export type SousCategorieMonBien = keyof typeof SOUS_CATEGORIES_MON_BIEN;

// ============================================
// FORMATS DE FICHIERS ACCEPTÉS (5 formats)
// ============================================

export const FORMATS_ACCEPTES = {
  'application/pdf': { extension: '.pdf', label: 'PDF' },
  'image/jpeg': { extension: '.jpg', label: 'JPEG' },
  'image/png': { extension: '.png', label: 'PNG' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { extension: '.docx', label: 'Word' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { extension: '.xlsx', label: 'Excel' },
} as const;

export type MimeTypeAccepte = keyof typeof FORMATS_ACCEPTES;

export const EXTENSIONS_ACCEPTEES = ['.pdf', '.jpg', '.jpeg', '.png', '.docx', '.xlsx'];

export const MIME_TYPES_ACCEPTES = Object.keys(FORMATS_ACCEPTES) as MimeTypeAccepte[];

// Pour l'attribut accept des inputs file
export const ACCEPT_INPUT_FILE = MIME_TYPES_ACCEPTES.join(',');

/**
 * Vérifie si un type MIME est accepté
 */
export function isFormatAccepte(mimeType: string): boolean {
  return mimeType in FORMATS_ACCEPTES;
}

/**
 * Vérifie si une extension est acceptée
 */
export function isExtensionAcceptee(filename: string): boolean {
  const ext = filename.toLowerCase().slice(filename.lastIndexOf('.'));
  return EXTENSIONS_ACCEPTEES.includes(ext);
}

// ============================================
// LIMITES DE TAILLE
// ============================================

export const LIMITES_TAILLE = {
  UPLOAD_DIRECT: 10 * 1024 * 1024,      // 10 Mo - upload sans alerte
  UPLOAD_AVEC_ALERTE: 25 * 1024 * 1024, // 25 Mo - upload avec alerte
  MAXIMUM: 25 * 1024 * 1024,            // 25 Mo - refus au-delà
} as const;

export type NiveauTaille = 'ok' | 'alerte' | 'refuse';

/**
 * Évalue le niveau de taille d'un fichier
 */
export function evaluerTailleFichier(tailleOctets: number): NiveauTaille {
  if (tailleOctets <= LIMITES_TAILLE.UPLOAD_DIRECT) return 'ok';
  if (tailleOctets <= LIMITES_TAILLE.MAXIMUM) return 'alerte';
  return 'refuse';
}

/**
 * Message d'erreur pour fichier trop volumineux
 */
export function getMessageTaille(niveau: NiveauTaille): string | null {
  switch (niveau) {
    case 'ok':
      return null;
    case 'alerte':
      return 'Document volumineux. L\'upload peut prendre du temps.';
    case 'refuse':
      return 'Document trop lourd (max 25 Mo). Compressez-le ou conservez-le sur votre Dropbox.';
  }
}

// ============================================
// VISIBILITÉ DES DOCUMENTS
// ============================================

export const VISIBILITES_DOCUMENT = {
  cs_only: 'Conseil Syndical uniquement',
  public: 'Visible par tous les copropriétaires',
} as const;

export type VisibiliteDocument = keyof typeof VISIBILITES_DOCUMENT;

export const VISIBILITE_PAR_DEFAUT: VisibiliteDocument = 'cs_only';

// ============================================
// STATUTS SENTINELLE
// ============================================

export const STATUTS_SENTINELLE = {
  en_attente: 'En attente de validation',
  accepte: 'Accepté',
  rejete: 'Rejeté',
  corrige: 'Corrigé par l\'utilisateur',
} as const;

export type StatutSentinelle = keyof typeof STATUTS_SENTINELLE;

// ============================================
// MAPPING ANCIEN → NOUVEAU TYPE (migration)
// ============================================

export const MAPPING_ANCIENS_TYPES: Record<string, { type: TypeDocumentGED; categorie: CategorieDocument }> = {
  correspondance_entrante: { type: 'courrier', categorie: 'administratif' },
  correspondance_sortante: { type: 'courrier', categorie: 'administratif' },
  pv_compte_rendu: { type: 'pv', categorie: 'juridique' },
  rapport_diagnostic: { type: 'rapport', categorie: 'technique' },
  document_technique: { type: 'plan', categorie: 'technique' },
  devis: { type: 'devis', categorie: 'comptabilite' },
  facture: { type: 'facture', categorie: 'comptabilite' },
  photo_video: { type: 'photo', categorie: 'travaux' },
  contrat_assurance: { type: 'contrat', categorie: 'assurance' },
  document_administratif: { type: 'attestation', categorie: 'administratif' },
  autre: { type: 'autre', categorie: 'administratif' },
};

// ============================================
// CONVENTION DE NOMMAGE
// ============================================

// Codes courts pour le nommage normalisé
export const CODES_TYPES: Record<TypeDocumentGED, string> = {
  pv: 'PV',
  contrat: 'CTR',
  facture: 'FAC',
  devis: 'DEV',
  courrier: 'COU',
  email: 'EML',
  rapport: 'RAP',
  plan: 'PLN',
  attestation: 'ATT',
  reglement: 'REG',
  edd: 'EDD',
  dpe: 'DPE',
  permis: 'PER',
  photo: 'PHO',
  note: 'NOT',
  // Session 67 : Codes types comptables
  annexe: 'ANN',
  appel_fonds: 'APF',
  budget: 'BUD',
  releve: 'RLV',
  // Session 166 : Codes types bailleur
  bail: 'BAI',
  assurance_pno: 'PNO',
  diagnostic_locatif: 'DIA',
  taxe_fonciere: 'TFO',
  etat_lieux: 'EDL',
  appel_loyer: 'APL',
  quittance_loyer: 'QUI',
  regularisation_charges: 'RCH',
  reglement_copropriete: 'RCP',
  autre: 'AUT',
};

/**
 * Génère un nom de fichier normalisé selon la convention
 * Format: AAAA-MM-JJ_TYPE_CAT_Description-courte.ext
 */
export function genererNomFichierNormalise(
  date: Date,
  type: TypeDocumentGED,
  categorie: CategorieDocument,
  description: string,
  extension: string
): string {
  const dateStr = date.toISOString().slice(0, 10); // AAAA-MM-JJ
  const typeCode = CODES_TYPES[type];
  const catCode = CODES_CATEGORIES[categorie];
  
  // Nettoyer la description : pas d'accents, pas de caractères spéciaux
  const descClean = description
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
    .replace(/[^a-zA-Z0-9\s-]/g, '') // Garde lettres, chiffres, espaces, tirets
    .trim()
    .replace(/\s+/g, '-') // Espaces → tirets
    .slice(0, 50); // Max 50 caractères
  
  // S'assurer que l'extension commence par un point
  const ext = extension.startsWith('.') ? extension : `.${extension}`;
  
  return `${dateStr}_${typeCode}_${catCode}_${descClean}${ext}`;
}

// ============================================
// TYPES DOCUMENTS COMPTABLES (Session 67)
// Pour faciliter le filtrage dans le module Vérification
// ============================================

export const TYPES_DOCUMENTS_COMPTABLES: TypeDocumentGED[] = [
  'annexe',
  'appel_fonds', 
  'budget',
  'releve',
  'facture',
  'devis',
];

/**
 * Vérifie si un type de document est comptable
 */
export function isDocumentComptable(type: TypeDocumentGED): boolean {
  return TYPES_DOCUMENTS_COMPTABLES.includes(type);
}

// ============================================
// TYPES DOCUMENTS BAILLEUR (Session 166)
// Pour faciliter le filtrage dans la catégorie "Mon bien"
// ============================================

export const TYPES_DOCUMENTS_BAILLEUR: TypeDocumentGED[] = [
  'bail',
  'assurance_pno',
  'diagnostic_locatif',
  'taxe_fonciere',
  'etat_lieux',
  'appel_loyer',
  'quittance_loyer',
  'regularisation_charges',
  'reglement_copropriete',
];

/**
 * Vérifie si un type de document est lié au bien / location
 */
export function isDocumentBailleur(type: TypeDocumentGED): boolean {
  return TYPES_DOCUMENTS_BAILLEUR.includes(type);
}

// ============================================
// MESSAGES D'ERREUR
// ============================================

export const MESSAGES_ERREUR_GED = {
  FORMAT_REFUSE: 'Format non accepté. La GED accepte uniquement : PDF, JPG, PNG, Word (.docx) et Excel (.xlsx). Convertissez votre fichier avant de l\'importer.',
  TAILLE_DEPASSEE: 'Document trop lourd (max 25 Mo). Compressez-le ou conservez-le sur votre Dropbox.',
  UPLOAD_ECHEC: 'Échec de l\'upload. Veuillez réessayer.',
  DOUBLON_DETECTE: 'Un document similaire existe déjà dans la GED.',
} as const;
